import { useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { ManufacturingModelsDictateMicButton } from "@/components/manufacturing/ManufacturingModelsDictateMicButton";
import { inputClass, mfgPrimaryBtnClass, ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  confirmBuilderAssistantSession,
  resolveBuilderAssistantModel,
  resolveBuilderAssistantSerial,
  startUnmatchedBuilderAssistantSession,
  type WaitingInspectionOrder,
} from "@/lib/manufacturing/builderAssistantClient";
import { parseQaSetupSpeech } from "@/lib/manufacturing/builderAssistantSpeech";
import { splitInspectionSerial } from "@/lib/manufacturing/inspectionSerial";
import type {
  BuilderAssistantModelMatch,
  BuilderAssistantSerialMatch,
} from "@/lib/manufacturing/builderAssistantTypes";
import { BuilderAssistantOrderPicker } from "./BuilderAssistantOrderPicker";

export function BuilderAssistantStart({
  onStarted,
}: {
  onStarted: (sessionId: string) => void;
}) {
  const [serial, setSerial] = useState("");
  const [longSerial, setLongSerial] = useState("");
  const [hint, setHint] = useState("");
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [matches, setMatches] = useState<BuilderAssistantSerialMatch[] | null>(null);
  const [models, setModels] = useState<BuilderAssistantModelMatch[] | null>(null);
  const [picking, setPicking] = useState(false);
  const [lookupMiss, setLookupMiss] = useState(false);
  const [askUnmatched, setAskUnmatched] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function applySerial(raw: string) {
    const split = splitInspectionSerial(raw);
    if (split.longSerial) {
      setSerial(split.serial);
      setLongSerial(split.longSerial);
    } else {
      setSerial(raw);
    }
    setUnitId(null);
    setLookupMiss(false);
    setAskUnmatched(false);
  }

  async function lookup() {
    setBusy(true);
    setError(null);
    setNote(null);
    setMatches(null);
    setModels(null);
    setLookupMiss(false);
    setAskUnmatched(false);
    const notes: string[] = [];
    if (serial.trim()) {
      const res = await resolveBuilderAssistantSerial(longSerial.trim() || serial);
      if (!res.ok) {
        setBusy(false);
        setError(res.error);
        return;
      }
      setMatches(res.data.matches);
      if (res.data.matches.length === 0) {
        setLookupMiss(true);
        notes.push("No order matched that serial.");
      }
    }
    if (hint.trim()) {
      const res = await resolveBuilderAssistantModel(hint);
      if (!res.ok) {
        setBusy(false);
        setError(res.error);
        return;
      }
      setModels(res.data.matches);
      if (res.data.matches.length === 0) {
        notes.push("No model matched. The text you typed stays on the inspection.");
      }
    }
    if (!serial.trim() && !hint.trim()) {
      notes.push("Add a serial or model if you have one, or start with just the condition.");
    }
    setNote(notes.join(" "));
    setBusy(false);
  }

  async function confirm(match: BuilderAssistantSerialMatch) {
    setBusy(true);
    setError(null);
    const split = splitInspectionSerial(match.serial);
    const res = await confirmBuilderAssistantSession(match.unit_id, hint, split.longSerial || longSerial);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onStarted(res.data.session.id);
  }

  async function startUnmatched() {
    setBusy(true);
    setError(null);
    setAskUnmatched(false);
    const res = await startUnmatchedBuilderAssistantSession({
      serialText: serial,
      modelHint: hint,
      recipeId,
      longSerial,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onStarted(res.data.session.id);
  }

  async function start() {
    if (unitId) {
      setBusy(true);
      setError(null);
      const res = await confirmBuilderAssistantSession(unitId, hint, longSerial);
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      onStarted(res.data.session.id);
      return;
    }
    if (lookupMiss) {
      setAskUnmatched(true);
      return;
    }
    await startUnmatched();
  }

  function pickOrder(order: WaitingInspectionOrder) {
    setSerial(order.serial);
    setLongSerial(order.long_serial);
    setHint(order.model ? [order.model, order.size].filter(Boolean).join(" ") : hint);
    setUnitId(order.unit_id);
    setLookupMiss(false);
    setAskUnmatched(false);
    setMatches(null);
    setPicking(false);
  }

  return (
    <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-zinc-700">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Start an inspection</h3>
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Type or dictate the short serial, or choose a waiting order. A match is optional. The long serial is kept when the order has one.
      </p>
      <div>
        <span className="text-xs font-medium text-gray-700 dark:text-zinc-300">Serial</span>
        <div className="mt-1 flex gap-2">
          <input
            className={`${inputClass} min-w-0 flex-1`}
            value={serial}
            aria-label="Serial"
            onChange={(event) => applySerial(event.target.value)}
            placeholder="56382"
          />
          <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => setPicking((open) => !open)}>
            Select Order
          </button>
        </div>
      </div>
      {picking ? (
        <BuilderAssistantOrderPicker busy={busy} onPick={pickOrder} onClose={() => setPicking(false)} />
      ) : null}
      <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
        Long serial
        <input
          className={`${inputClass} mt-1 w-full`}
          value={longSerial}
          aria-label="Long serial"
          onChange={(event) => setLongSerial(event.target.value)}
          placeholder="CL-UT-WP-1428-033126-056382"
        />
      </label>
      <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
        Model
        <input
          className={`${inputClass} mt-1 w-full`}
          value={hint}
          onChange={(event) => {
            setHint(event.target.value);
            setRecipeId(null);
          }}
          placeholder="Metal building"
        />
      </label>
      <ManufacturingModelsDictateMicButton
        extractLabel="Use speech"
        disabled={busy}
        help={
          <InlineHelp glyph="!" label="How to dictate a serial and model">
            Say the serial, then the building. Example: this is serial number 059123. It is a 12 by 20
            silver bullet. A size said as 12 by 20 is saved as 12x20.
          </InlineHelp>
        }
        onComplete={(transcript) => {
          const split = parseQaSetupSpeech(transcript);
          applySerial(split.serial);
          setHint(split.model);
          setRecipeId(null);
        }}
      />
      <div className="flex flex-wrap gap-2">
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void lookup()}>
          Look up
        </button>
        <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={() => void start()}>
          Start inspection
        </button>
      </div>
      {askUnmatched ? (
        <div className="space-y-2 rounded-md border border-gray-200 p-3 text-sm dark:border-zinc-700">
          <p className="text-gray-900 dark:text-zinc-100">
            No order matched {serial || "that serial"}. Start a checklist that is not linked to a unit?
          </p>
          <div className="flex gap-2">
            <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={() => void startUnmatched()}>
              Start checklist
            </button>
            <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => setAskUnmatched(false)}>
              Keep looking
            </button>
          </div>
        </div>
      ) : null}
      {note ? <p className="text-sm text-gray-700 dark:text-zinc-300">{note}</p> : null}
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {matches && matches.length > 0 ? (
        <ul className="space-y-2">
          {matches.map((match) => {
            const split = splitInspectionSerial(match.serial);
            return (
              <li key={match.unit_id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-gray-900 dark:text-zinc-100">
                  Unit {split.serial || match.serial}
                  {split.longSerial ? <span className="block text-xs text-gray-600 dark:text-zinc-400">{split.longSerial}</span> : null}
                </span>
                <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void confirm(match)}>
                  Use this unit
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {models && models.length > 0 ? (
        <ul className="space-y-2">
          {models.map((model) => (
            <li key={model.recipe_id} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-gray-900 dark:text-zinc-100">
                {model.name}
                {model.recipe_code ? ` (${model.recipe_code})` : ""}
              </span>
              <button
                type="button"
                className={ghostBtnClass}
                disabled={busy}
                onClick={() => {
                  setHint(model.name);
                  setRecipeId(model.recipe_id);
                }}
              >
                {recipeId === model.recipe_id ? "Selected" : "Use this model"}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
